// "Eriol Bot" AI layer — renderer-side companion to the extension
// server's deterministic BotEngine.
//
// Two features, both driven by the live chat SSE stream
// (`eventBusService.onChat`, the same source `eventBusBridge` uses):
//
//   1. Semantic moderation — batches incoming messages (up to
//      MAX_BATCH msgs / BATCH_WINDOW_MS window), classifies them with
//      an LLM returning strict JSON verdicts, and applies the
//      configured moderation action (delete / timeout) through
//      `botService.moderateChat`. Broadcaster-authored messages,
//      exempt roles (from GET /api/bot/filters) and command messages
//      (starting with the bot prefix) are never classified.
//
//   2. `!ask` command — when a message is `<prefix>ask <question>`
//      (prefix from GET /api/bot/settings, default "!"), asks the LLM
//      for a short answer (≤350 chars) and replies on the originating
//      platform prefixed `@author `. Cooldowns: 30s per user, 10s
//      global, kept in memory.
//
// LLM access is a minimal direct client (no SDK deps):
//   - OpenAI-compatible /chat/completions for OpenAI / DeepSeek /
//     OpenRouter / Ollama
//   - Anthropic Messages API for Claude
//   - Gemini generateContent for Google
//
// Provider/model/key can be prefilled from the AI Agent's stored
// settings (the agent server persists `openaiApiKey` + `llmBaseUrl` +
// `llmModel` at /api/agent/settings — see ModalAgent), via
// `fetchAgentLlmPrefill()`.
//
// Config persists in localStorage under "@Eriol/bot/ai.config",
// following the app's "@Eriol/..." key convention (same family as
// the local API port key in `lib/localApi`).

import AgentService from "../../infrastructure/Services/AgentService";

import { eventBusService } from "../eventBus/eventBusService";

import type { ChatMessage } from "../eventBus/types";

import botService from "./botService";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export type AiProvider =
  | "openai"
  | "gemini"
  | "claude"
  | "deepseek"
  | "openrouter"
  | "ollama";

export type AiModerationAction = "delete" | "timeout";

export interface AiBotConfig {
  provider: AiProvider;
  model: string;
  apiKey: string;
  moderation: {
    enabled: boolean;
    action: AiModerationAction;
    timeoutSeconds: number;
    /** Extra rules appended verbatim to the classifier system prompt. */
    criteria: string;
  };
  ask: {
    enabled: boolean;
    /** Reply language: "es" | "en" | "pt". */
    language: string;
  };
}

export const AI_BOT_CONFIG_KEY = "@Eriol/bot/ai.config";

export const DEFAULT_AI_BOT_CONFIG: AiBotConfig = {
  provider: "openai",
  model: "gpt-4o-mini",
  apiKey: "",
  moderation: {
    enabled: false,
    action: "delete",
    timeoutSeconds: 600,
    criteria: "",
  },
  ask: {
    enabled: false,
    language: "es",
  },
};

/** Provider metadata for the settings UI + the LLM client. */
export const AI_PROVIDERS: Record<
  AiProvider,
  {
    label: string;
    defaultModel: string;
    keyPlaceholder: string;
    /** Suggested models for the UI select. */
    models: { value: string; label: string }[];
  }
> = {
  openai: {
    label: "OpenAI",
    defaultModel: "gpt-4o-mini",
    keyPlaceholder: "sk-...",
    models: [
      { value: "gpt-4o-mini", label: "GPT-4o Mini" },
      { value: "gpt-4o", label: "GPT-4o" },
      { value: "gpt-4.1-mini", label: "GPT-4.1 Mini" },
      { value: "gpt-4.1", label: "GPT-4.1" },
    ],
  },
  gemini: {
    label: "Google Gemini",
    defaultModel: "gemini-2.0-flash",
    keyPlaceholder: "AIza...",
    models: [
      { value: "gemini-2.0-flash", label: "Gemini 2.0 Flash" },
      { value: "gemini-2.0-flash-lite", label: "Gemini 2.0 Flash Lite" },
      { value: "gemini-2.5-flash-preview-04-17", label: "Gemini 2.5 Flash" },
    ],
  },
  claude: {
    label: "Claude",
    defaultModel: "claude-haiku-4-20250414",
    keyPlaceholder: "sk-ant-...",
    models: [
      { value: "claude-haiku-4-20250414", label: "Claude Haiku 4" },
      { value: "claude-sonnet-4-20250514", label: "Claude Sonnet 4" },
    ],
  },
  deepseek: {
    label: "DeepSeek",
    defaultModel: "deepseek-chat",
    keyPlaceholder: "sk-...",
    models: [{ value: "deepseek-chat", label: "DeepSeek Chat (V3)" }],
  },
  openrouter: {
    label: "OpenRouter",
    defaultModel: "openai/gpt-4o-mini",
    keyPlaceholder: "sk-or-...",
    models: [
      { value: "openai/gpt-4o-mini", label: "GPT-4o Mini" },
      { value: "anthropic/claude-haiku-4", label: "Claude Haiku 4" },
      { value: "google/gemini-2.0-flash-001", label: "Gemini 2.0 Flash" },
      { value: "deepseek/deepseek-chat-v3-0324", label: "DeepSeek V3" },
    ],
  },
  ollama: {
    label: "Ollama",
    defaultModel: "llama3.1",
    keyPlaceholder: "(no requiere clave)",
    models: [
      { value: "llama3.1", label: "Llama 3.1" },
      { value: "qwen2.5", label: "Qwen 2.5" },
      { value: "mistral", label: "Mistral" },
    ],
  },
};

export const loadAiBotConfig = (): AiBotConfig => {
  try {
    const raw = localStorage.getItem(AI_BOT_CONFIG_KEY);
    if (!raw) return structuredClone(DEFAULT_AI_BOT_CONFIG);
    const parsed = JSON.parse(raw) as Partial<AiBotConfig>;
    return {
      ...structuredClone(DEFAULT_AI_BOT_CONFIG),
      ...parsed,
      moderation: {
        ...DEFAULT_AI_BOT_CONFIG.moderation,
        ...(parsed.moderation ?? {}),
      },
      ask: { ...DEFAULT_AI_BOT_CONFIG.ask, ...(parsed.ask ?? {}) },
    };
  } catch {
    return structuredClone(DEFAULT_AI_BOT_CONFIG);
  }
};

export const saveAiBotConfig = (config: AiBotConfig): void => {
  localStorage.setItem(AI_BOT_CONFIG_KEY, JSON.stringify(config));
};

// ---------------------------------------------------------------------------
// Agent-config prefill
// ---------------------------------------------------------------------------

export interface AgentLlmPrefill {
  provider: AiProvider;
  model: string;
  apiKey: string;
}

const hostMatches = (hostname: string, domain: string): boolean =>
  hostname === domain || hostname.endsWith(`.${domain}`);

const providerFromBaseUrl = (baseUrl: string): AiProvider => {
  let parsed: URL;
  try {
    parsed = new URL(baseUrl);
  } catch {
    return "openai";
  }
  const host = parsed.hostname.toLowerCase();
  if (
    hostMatches(host, "googleapis.com") &&
    host.startsWith("generativelanguage")
  )
    return "gemini";
  if (hostMatches(host, "anthropic.com")) return "claude";
  if (hostMatches(host, "deepseek.com")) return "deepseek";
  if (hostMatches(host, "openrouter.ai")) return "openrouter";
  if (parsed.port === "11434") return "ollama";
  return "openai";
};

/**
 * Reads the LLM provider config the AI Agent (ModalAgent) stored on the
 * local agent server (`GET /api/agent/settings` — `openaiApiKey` +
 * `llmBaseUrl` + `llmModel`). Returns null when the agent server isn't
 * running or no key was ever configured.
 */
export const fetchAgentLlmPrefill =
  async (): Promise<AgentLlmPrefill | null> => {
    try {
      const settings = (await new AgentService().getSettings()) as {
        openaiApiKey?: string;
        llmBaseUrl?: string;
        llmModel?: string;
      };
      const apiKey = settings.openaiApiKey ?? "";
      if (!apiKey) return null;
      const provider = providerFromBaseUrl(settings.llmBaseUrl ?? "");
      return {
        provider,
        model: settings.llmModel || AI_PROVIDERS[provider].defaultModel,
        apiKey: apiKey === "ollama" ? "" : apiKey,
      };
    } catch {
      return null;
    }
  };

// ---------------------------------------------------------------------------
// Minimal multi-provider chat-completions client
// ---------------------------------------------------------------------------

interface LlmCallOptions {
  system: string;
  user: string;
  maxTokens?: number;
  temperature?: number;
}

const OPENAI_COMPATIBLE_BASE: Partial<Record<AiProvider, string>> = {
  openai: "https://api.openai.com/v1",
  deepseek: "https://api.deepseek.com/v1",
  openrouter: "https://openrouter.ai/api/v1",
  ollama: "http://127.0.0.1:11434/v1",
};

const postJson = async (
  url: string,
  headers: Record<string, string>,
  body: unknown,
): Promise<unknown> => {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `LLM request failed (${response.status})${
        detail ? `: ${detail.slice(0, 200)}` : ""
      }`,
    );
  }
  return response.json();
};

/**
 * Single-turn chat completion against the configured provider.
 * Returns the assistant text (trimmed). Throws on transport/HTTP errors.
 */
export const chatComplete = async (
  config: Pick<AiBotConfig, "provider" | "model" | "apiKey">,
  options: LlmCallOptions,
): Promise<string> => {
  const { provider, model, apiKey } = config;
  const maxTokens = options.maxTokens ?? 300;
  const temperature = options.temperature ?? 0.3;

  if (provider === "claude") {
    const json = (await postJson(
      "https://api.anthropic.com/v1/messages",
      {
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        // Required for direct browser/renderer calls.
        "anthropic-dangerous-direct-browser-access": "true",
      },
      {
        model,
        max_tokens: maxTokens,
        temperature,
        system: options.system,
        messages: [{ role: "user", content: options.user }],
      },
    )) as { content?: { type?: string; text?: string }[] };
    return (json.content ?? [])
      .map((block) => block.text ?? "")
      .join("")
      .trim();
  }

  if (provider === "gemini") {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(
      model,
    )}:generateContent?key=${encodeURIComponent(apiKey)}`;
    const json = (await postJson(
      url,
      {},
      {
        systemInstruction: { parts: [{ text: options.system }] },
        contents: [{ role: "user", parts: [{ text: options.user }] }],
        generationConfig: { maxOutputTokens: maxTokens, temperature },
      },
    )) as {
      candidates?: { content?: { parts?: { text?: string }[] } }[];
    };
    return (json.candidates?.[0]?.content?.parts ?? [])
      .map((part) => part.text ?? "")
      .join("")
      .trim();
  }

  // OpenAI-compatible providers (OpenAI / DeepSeek / OpenRouter / Ollama)
  const base =
    OPENAI_COMPATIBLE_BASE[provider] ?? OPENAI_COMPATIBLE_BASE.openai;
  const json = (await postJson(
    `${base}/chat/completions`,
    { Authorization: `Bearer ${apiKey || "ollama"}` },
    {
      model,
      max_tokens: maxTokens,
      temperature,
      messages: [
        { role: "system", content: options.system },
        { role: "user", content: options.user },
      ],
    },
  )) as { choices?: { message?: { content?: string } }[] };
  return (json.choices?.[0]?.message?.content ?? "").trim();
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MAX_BATCH = 5;
const BATCH_WINDOW_MS = 3_000;
const ASK_USER_COOLDOWN_MS = 30_000;
const ASK_GLOBAL_COOLDOWN_MS = 10_000;
const ASK_MAX_CHARS = 350;
const SERVER_REFRESH_MS = 60_000;

const hasBadge = (badges: string[], role: string): boolean =>
  badges.some((badge) => {
    const normalized = badge.trim().toLowerCase();
    return normalized === role || normalized.startsWith(`${role}/`);
  });

const isBroadcaster = (msg: ChatMessage): boolean =>
  hasBadge(msg.authorBadges ?? [], "broadcaster") ||
  hasBadge(msg.authorBadges ?? [], "owner");

const ASK_LANGUAGES: Record<string, string> = {
  es: "Spanish",
  en: "English",
  pt: "Portuguese",
};

const stripCodeFences = (text: string): string => {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  return (fenced ? fenced[1] : text).trim();
};

export interface AiBotStats {
  classifiedToday: number;
  flaggedToday: number;
}

interface Verdict {
  id?: unknown;
  flag?: unknown;
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

class AiBotEngine {
  private config: AiBotConfig = loadAiBotConfig();

  private started = false;

  private unsubChat: (() => void) | null = null;

  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  // Cached server-side bot state (prefix + exempt roles).
  private prefix = "!";

  private exemptRoles: string[] = [];

  // Moderation batching
  private queue: ChatMessage[] = [];

  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  private classifying = false;

  // !ask cooldowns
  private askUserCooldown = new Map<string, number>();

  private askGlobalAt = 0;

  // Daily counters (in-memory; reset on date change)
  private statsDay = new Date().toDateString();

  private classifiedToday = 0;

  private flaggedToday = 0;

  /** Idempotent. Subscribes to live chat and starts the server-state poll. */
  start(): void {
    if (this.started) return;
    this.started = true;
    eventBusService.start();
    this.unsubChat = eventBusService.onChat((msg) => this.handleChat(msg));
    void this.refreshServerState();
    this.refreshTimer = setInterval(() => {
      void this.refreshServerState();
    }, SERVER_REFRESH_MS);
  }

  stop(): void {
    this.unsubChat?.();
    this.unsubChat = null;
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.refreshTimer = null;
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    this.queue = [];
    this.started = false;
  }

  /** Persist + hot-apply a new config (called from the IA tab on save). */
  applyConfig(config: AiBotConfig): void {
    this.config = config;
    saveAiBotConfig(config);
  }

  getConfig(): AiBotConfig {
    return this.config;
  }

  getStats(): AiBotStats {
    this.rollStatsDay();
    return {
      classifiedToday: this.classifiedToday,
      flaggedToday: this.flaggedToday,
    };
  }

  // -- internals -------------------------------------------------------------

  private rollStatsDay(): void {
    const today = new Date().toDateString();
    if (today !== this.statsDay) {
      this.statsDay = today;
      this.classifiedToday = 0;
      this.flaggedToday = 0;
    }
  }

  private get anyEnabled(): boolean {
    return this.config.moderation.enabled || this.config.ask.enabled;
  }

  private async refreshServerState(): Promise<void> {
    if (!this.anyEnabled) return;
    try {
      const [settings, filters] = await Promise.all([
        botService.getSettings(),
        botService.getFilters(),
      ]);
      if (settings?.prefix) this.prefix = settings.prefix;
      this.exemptRoles = (filters?.exemptRoles ?? []).map((role) =>
        role.toLowerCase(),
      );
    } catch {
      // Extension may be down — keep last known prefix/roles.
    }
  }

  private handleChat(msg: ChatMessage): void {
    if (!this.anyEnabled) return;

    const text = (msg.text ?? "").trim();
    if (!text) return;

    // `<prefix>ask <question>` — handled by the AI layer; everything
    // starting with the prefix is a command and is never classified.
    const askPrefix = `${this.prefix}ask`;
    if (
      this.config.ask.enabled &&
      text.toLowerCase().startsWith(`${askPrefix.toLowerCase()} `)
    ) {
      void this.handleAsk(msg, text.slice(askPrefix.length).trim());
      return;
    }

    if (text.startsWith(this.prefix)) return;

    if (!this.config.moderation.enabled) return;
    if (!this.config.apiKey && this.config.provider !== "ollama") return;

    // Broadcaster + exempt roles never get classified. Bot-sent
    // messages are broadcaster-authored, so this also prevents the AI
    // from moderating the bot itself.
    if (isBroadcaster(msg)) return;
    const badges = msg.authorBadges ?? [];
    if (this.exemptRoles.some((role) => hasBadge(badges, role))) return;

    this.queue.push(msg);
    if (this.queue.length >= MAX_BATCH) {
      void this.flushModeration();
    } else if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => {
        void this.flushModeration();
      }, BATCH_WINDOW_MS);
    }
  }

  private buildModerationSystemPrompt(): string {
    const custom = this.config.moderation.criteria.trim();
    return [
      "You are a strict, fair chat moderation classifier for a livestream chat.",
      "You receive a JSON array of chat messages. Classify EVERY message.",
      'Respond ONLY with minified JSON, no prose, no markdown: {"verdicts":[{"id":"<message id>","flag":"ok"|"toxic"|"spam"}]}',
      'Use "toxic" for harassment, hate speech, slurs, threats, sexual content directed at people.',
      'Use "spam" for scams, phishing, "free followers/viewers" offers, crypto giveaway spam, repeated self-promotion.',
      'Use "ok" for everything else. Mild banter, criticism and emotes are "ok". When unsure, answer "ok".',
      custom ? `Additional rules from the streamer:\n${custom}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }

  private async flushModeration(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.queue.length === 0 || this.classifying) return;

    const batch = this.queue.splice(0, MAX_BATCH);
    this.classifying = true;
    try {
      const user = JSON.stringify(
        batch.map((m) => ({
          id: m.id,
          user: m.authorName,
          text: m.text.slice(0, 500),
        })),
      );

      const raw = await chatComplete(this.config, {
        system: this.buildModerationSystemPrompt(),
        user,
        maxTokens: 300,
        temperature: 0,
      });

      const parsed = JSON.parse(stripCodeFences(raw)) as {
        verdicts?: Verdict[];
      };
      const verdicts = Array.isArray(parsed.verdicts) ? parsed.verdicts : [];

      this.rollStatsDay();
      this.classifiedToday += batch.length;

      for (const verdict of verdicts) {
        const flag = typeof verdict.flag === "string" ? verdict.flag : "ok";
        if (flag !== "toxic" && flag !== "spam") continue;
        const target = batch.find((m) => m.id === verdict.id);
        if (!target) continue;
        this.flaggedToday += 1;
        await this.applyModeration(target);
      }
    } catch (error) {
      console.error("[aiBot] moderation classify failed", error);
    } finally {
      this.classifying = false;
      // More messages may have queued while we were classifying.
      if (this.queue.length >= MAX_BATCH) {
        void this.flushModeration();
      } else if (this.queue.length > 0 && !this.flushTimer) {
        this.flushTimer = setTimeout(() => {
          void this.flushModeration();
        }, BATCH_WINDOW_MS);
      }
    }
  }

  private async applyModeration(msg: ChatMessage): Promise<void> {
    const action = this.config.moderation.action;
    try {
      await botService.moderateChat({
        platform: msg.platform,
        action,
        messageId: msg.id,
        userId: msg.authorId || undefined,
        userName: msg.authorName || undefined,
        ...(action === "timeout"
          ? {
              durationSeconds:
                this.config.moderation.timeoutSeconds > 0
                  ? this.config.moderation.timeoutSeconds
                  : 600,
            }
          : {}),
      });
    } catch (error) {
      console.error("[aiBot] moderation action failed", error);
    }
  }

  private async handleAsk(msg: ChatMessage, question: string): Promise<void> {
    if (!question) return;
    if (!this.config.apiKey && this.config.provider !== "ollama") return;

    const now = Date.now();
    if (now - this.askGlobalAt < ASK_GLOBAL_COOLDOWN_MS) return;
    const userKey = msg.authorId || msg.authorName;
    const lastUser = this.askUserCooldown.get(userKey) ?? 0;
    if (now - lastUser < ASK_USER_COOLDOWN_MS) return;

    // Reserve the cooldown slots up-front so overlapping requests from
    // the same window don't double-fire while the LLM call is in flight.
    this.askGlobalAt = now;
    this.askUserCooldown.set(userKey, now);

    const language =
      ASK_LANGUAGES[this.config.ask.language] ?? this.config.ask.language;

    try {
      const answer = await chatComplete(this.config, {
        system: [
          "You are the chat assistant of a livestream. A viewer asked you a question.",
          `Answer briefly and helpfully in at most ${ASK_MAX_CHARS} characters of plain text — no markdown, no links unless asked.`,
          `Always answer in ${language}.`,
        ].join("\n"),
        user: question.slice(0, 500),
        maxTokens: 220,
        temperature: 0.5,
      });

      if (!answer) return;

      const reply = `@${msg.authorName} ${answer}`.slice(0, ASK_MAX_CHARS + 50);

      await botService.sendChatMessage({
        platform: msg.platform,
        text: reply,
      });
    } catch (error) {
      console.error("[aiBot] !ask failed", error);
    }
  }
}

export const aiBotService = new AiBotEngine();
export default aiBotService;
